import React, { useEffect, useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';

import {
  onForward,
  DutyTypeOptions,
  PaymentTypeOptions,
  linexInformationValidation,
  DeclareCurrencyTypeOptions,
} from './constants';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const LinexInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, linexDetails } = useSelector((state: RootStateType) => state.adminShipment);

  const linexSettings = useSelector((state: RootStateType) => state.user.carrierSetting.linex);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      dutyType: '',
      PaymentType: '',
      // serviceCode: '',
      declareCurrency: '',
      ...initialStates,
    },
    validationSchema: linexInformationValidation,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        Linex Information
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="Linex Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={linexSettings?.accountNo?.length ? linexSettings?.accountNo : accountOptions}
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="dutyType"
            label="Duty Type"
            required
            placeholder="Select"
            name="dutyType"
            value={values.dutyType ? [values.dutyType] : []}
            options={DutyTypeOptions}
            onChange={val => setFieldValue('dutyType', val[0] || '')}
            error={errors.dutyType as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="PaymentType"
            label="Payment Type"
            required
            placeholder="Select"
            name="PaymentType"
            value={values.PaymentType ? [values.PaymentType] : []}
            options={PaymentTypeOptions}
            onChange={val => setFieldValue('PaymentType', val[0] || '')}
            error={errors.PaymentType as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="declareCurrency"
            label="Declare Currency"
            required
            placeholder="Select"
            name="declareCurrency"
            value={values.declareCurrency ? [values.declareCurrency] : []}
            options={DeclareCurrencyTypeOptions}
            onChange={val => setFieldValue('declareCurrency', val[0] || '')}
            error={errors.declareCurrency as string}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {linexDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default LinexInformation;
